import { notification } from "antd";
import classNames from "classnames";
import { get } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronLeft } from "react-icons/bi";
import {
  MdAdd,
  MdDeleteOutline,
  MdOutlineCloudUpload,
  MdOutlineExpandLess,
  MdOutlineExpandMore,
  MdOutlineMap,
  MdOutlineVisibility,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import Header from "shared/components/Header";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  ADD_REFERENTIAL,
  DELETE_REFERENTIAL,
  IMPORT_REFERENTIAL_FROM_FILE,
} from "utils/api/graphql/mutations/geo-zones";
import { REFERENTIALS } from "utils/api/graphql/queries/geo-zones";
import { SCREENS } from "utils/constants";

const TYPE_OPTIONS = [
  { value: "geo_zone", labelKey: "TYPE_GEO_ZONE" },
  { value: "regime", labelKey: "TYPE_REGIME" },
  { value: "beneficiary_type", labelKey: "TYPE_BENEFICIARY" },
];

const ReferentialsPage = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const REF = t("REFERENTIAL", { returnObjects: true });
  const navigate = useNavigate();

  const [filterType, setFilterType] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("geo_zone");
  const [createMode, setCreateMode] = useState("upload");
  const [manualEntries, setManualEntries] = useState([{ key: "", value: "" }]);
  const [expandedId, setExpandedId] = useState(null);
  const fileInputRef = useRef(null);

  const whereClause = filterType ? { type: filterType } : {};
  const { data, refetch } = useQuery(REFERENTIALS, {
    variables: {
      where: whereClause,
      take: 100,
      orderBy: [{ createdDate: "DESC" }],
    },
  });

  const referentials = get(data, "referentials.data", []);

  const [addReferential, { loading: addingManual }] = useMutation(
    ADD_REFERENTIAL,
    {
      onCompleted: () => {
        notification.success({ message: REF.CREATED });
        setShowCreate(false);
        setNewName("");
        setManualEntries([{ key: "", value: "" }]);
        refetch();
      },
      onError: (err) =>
        notification.error({
          message: REF.CREATE_ERROR,
          description: err.message,
        }),
    },
  );

  const [importReferential, { loading: importing }] = useMutation(
    IMPORT_REFERENTIAL_FROM_FILE,
    {
      onCompleted: () => {
        notification.success({ message: REF.CREATED });
        setShowCreate(false);
        setNewName("");
        refetch();
      },
      onError: (err) =>
        notification.error({
          message: REF.CREATE_ERROR,
          description: err.message,
        }),
    },
  );

  const [deleteReferential] = useMutation(DELETE_REFERENTIAL, {
    onCompleted: () => {
      notification.success({ message: REF.DELETED });
      refetch();
    },
    onError: (err) =>
      notification.error({
        message: REF.DELETE_ERROR,
        description: err.message,
      }),
  });

  const handleCreateManual = () => {
    const validEntries = manualEntries.filter((e) => e.key && e.value);
    if (!newName || validEntries.length === 0) return;

    const entries = {};
    if (newType === "geo_zone") {
      for (const entry of validEntries) {
        if (!entries[entry.key]) entries[entry.key] = [];
        if (!entries[entry.key].includes(entry.value)) {
          entries[entry.key].push(entry.value);
        }
      }
    } else {
      for (const entry of validEntries) {
        entries[entry.key] = entry.value;
      }
    }

    addReferential({
      variables: {
        data: {
          name: newName,
          type: newType,
          entries,
        },
      },
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !newName) return;

    const reader = new FileReader();
    reader.onload = () => {
      importReferential({
        variables: {
          data: {
            name: newName,
            type: newType,
            base64: reader.result,
            filename: file.name,
          },
        },
      });
    };
    reader.readAsDataURL(file);
  };

  const addEntry = () => {
    setManualEntries([...manualEntries, { key: "", value: "" }]);
  };

  const removeEntry = (idx) => {
    setManualEntries(manualEntries.filter((_, i) => i !== idx));
  };

  const updateEntry = (idx, field, value) => {
    const updated = [...manualEntries];
    updated[idx] = { ...updated[idx], [field]: value };
    setManualEntries(updated);
  };

  const getTypeLabel = (type) => {
    const opt = TYPE_OPTIONS.find((o) => o.value === type);
    return opt ? REF[opt.labelKey] || type : type;
  };

  const getEntryStats = (ref) => {
    const entries = ref.entries || {};
    const keys = Object.keys(entries);
    return { count: keys.length };
  };

  const renderEntries = (ref) => {
    const entries = ref.entries || {};
    if (ref.type === "geo_zone") {
      return (
        <table className="pricers-table">
          <thead>
            <tr className="pricers-table__header-row">
              <th className="pricers-table__th">{REF.ZONE_HEADER}</th>
              <th className="pricers-table__th">{REF.DEPARTMENTS}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(entries).map(([zone, departments]) => (
              <tr key={zone} className="pricers-table__row">
                <td className="pricers-table__td pricers-table__td--bold">
                  {zone}
                </td>
                <td className="pricers-table__td">
                  <div className="d-flex gap-4 flex--wrap">
                    {(Array.isArray(departments) ? departments : [departments])
                      .sort()
                      .map((dept) => (
                        <span key={dept} className="pricers-ref__dept-tag">
                          {dept}
                        </span>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <table className="pricers-table">
        <thead>
          <tr className="pricers-table__header-row">
            <th className="pricers-table__th">{REF.KEY_LABEL}</th>
            <th className="pricers-table__th">{REF.VALUE_LABEL}</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(entries).map(([key, value]) => (
            <tr key={key} className="pricers-table__row">
              <td className="pricers-table__td pricers-table__td--mono">
                {key}
              </td>
              <td className="pricers-table__td">{String(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const typeFilterOptions = [
    { label: REF.ALL_TYPES, value: "" },
    ...TYPE_OPTIONS.map((o) => ({
      label: REF[o.labelKey] || o.value,
      value: o.value,
    })),
  ];

  const typeSelectOptions = TYPE_OPTIONS.map((o) => ({
    label: REF[o.labelKey] || o.value,
    value: o.value,
  }));

  return (
    <>
      <Header
        title={REF.PAGE_TITLE}
        subtitle={REF.PAGE_SUBTITLE}
        actions={[
          {
            text: REF.BACK,
            onClick: () => navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path),
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: REF.NEW,
            onClick: () => setShowCreate(!showCreate),
            type: "primary",
            icon: <MdAdd size={20} />,
          },
        ]}
      />

      <div className="m-top-16 pricers-ref__filter-bar">
        <span className="pricers-ref__filter-label">{REF.FILTER_BY_TYPE}</span>
        <Select
          options={typeFilterOptions}
          value={filterType || ""}
          onChange={(val) => setFilterType(val || null)}
          className="w-220"
        />
      </div>

      {showCreate && (
        <Card type="simple" className="m-top-24">
          <h3 className="m-bottom-16">{REF.CREATE_TITLE}</h3>
          <div className="pricers-ref__create-row">
            <div className="flex--grow-1">
              <Input
                label={REF.NAME_LABEL}
                placeholder={REF.NAME_PLACEHOLDER}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="flex--grow-1">
              <label className="pricers-ref__form-label">
                {REF.TYPE_LABEL}
              </label>
              <Select
                options={typeSelectOptions}
                value={newType}
                onChange={setNewType}
                className="w-100"
              />
            </div>
          </div>

          {newType === "geo_zone" && (
            <div className="grid--2 gap-12 mt-16 mb-16">
              <div
                onClick={() => setCreateMode("upload")}
                className={classNames("pricers-import__mode-card", {
                  "pricers-import__mode-card--active": createMode === "upload",
                })}
              >
                <MdOutlineCloudUpload size={24} color="#4c6ef5" />
                <div className="pricers-import__mode-label">
                  {REF.UPLOAD_FILE}
                </div>
              </div>
              <div
                onClick={() => setCreateMode("manual")}
                className={classNames("pricers-import__mode-card", {
                  "pricers-import__mode-card--active": createMode === "manual",
                })}
              >
                <MdAdd size={24} color="#4c6ef5" />
                <div className="pricers-import__mode-label">
                  {REF.MANUAL_ENTRY}
                </div>
              </div>
            </div>
          )}

          {newType === "geo_zone" && createMode === "upload" && (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx,.csv"
                className="hidden-input"
                onChange={handleFileUpload}
              />
              <Button
                type="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={!newName || importing}
                loading={importing}
              >
                <MdOutlineCloudUpload size={20} />
                {REF.SELECT_FILE}
              </Button>
              <p className="pricers-import__file-hint">{REF.FILE_HINT}</p>
            </div>
          )}

          {(newType !== "geo_zone" ||
            (newType === "geo_zone" && createMode === "manual")) && (
            <div>
              {manualEntries.map((entry, idx) => (
                <div key={idx} className="pricers-import__entry-row">
                  <Input
                    placeholder={REF.KEY_PLACEHOLDER}
                    value={entry.key}
                    onChange={(e) => updateEntry(idx, "key", e.target.value)}
                    className="flex--grow-1"
                  />
                  <Input
                    placeholder={REF.VALUE_PLACEHOLDER}
                    value={entry.value}
                    onChange={(e) => updateEntry(idx, "value", e.target.value)}
                    className="flex--grow-1"
                  />
                  <MdDeleteOutline
                    size={20}
                    color="#e53935"
                    className="pricers-import__clickable"
                    onClick={() => removeEntry(idx)}
                  />
                </div>
              ))}
              <div className="d-flex gap-8 mt-12">
                <Button type="primary--outlined" onClick={addEntry}>
                  <MdAdd size={18} />
                  {REF.ADD_ROW}
                </Button>
                <Button
                  type="primary"
                  onClick={handleCreateManual}
                  disabled={!newName || addingManual}
                  loading={addingManual}
                >
                  {REF.CREATE_BUTTON}
                </Button>
              </div>
            </div>
          )}

          <div className="d-flex justify--end m-top-16">
            <Button
              type="default"
              onClick={() => {
                setShowCreate(false);
                setNewName("");
              }}
            >
              {REF.CANCEL}
            </Button>
          </div>
        </Card>
      )}

      <div className="m-top-24">
        {referentials.length === 0 ? (
          <Card type="simple">
            <div className="pricers-ref__empty">
              <MdOutlineMap size={48} color="#ccc" />
              <h3 className="pricers-ref__empty-title">{REF.EMPTY_TITLE}</h3>
              <p className="pricers-ref__empty-description">
                {REF.EMPTY_DESCRIPTION}
              </p>
              <Button
                type="primary"
                onClick={() => setShowCreate(true)}
                className="mt-16"
              >
                <MdAdd size={20} />
                {REF.CREATE_FIRST}
              </Button>
            </div>
          </Card>
        ) : (
          <div className="pricers-ref__card-list">
            {referentials.map((ref) => {
              const stats = getEntryStats(ref);
              const isExpanded = expandedId === ref.id;

              return (
                <Card key={ref.id} type="simple">
                  <div className="pricers-ref__card-row">
                    <MdOutlineMap size={24} color="#4c6ef5" />
                    <div className="flex--grow-1">
                      <strong>{ref.name}</strong>
                      <div className="pricers-ref__card-meta">
                        <span className="pricers-ref__type-badge">
                          {getTypeLabel(ref.type)}
                        </span>
                        {stats.count} {REF.ENTRIES}
                      </div>
                    </div>
                    <Button
                      type="primary--outlined"
                      onClick={() => setExpandedId(isExpanded ? null : ref.id)}
                    >
                      {isExpanded ? (
                        <MdOutlineExpandLess size={20} />
                      ) : (
                        <MdOutlineVisibility size={20} />
                      )}
                    </Button>
                    <Button
                      type="danger--outlined"
                      onClick={() =>
                        deleteReferential({
                          variables: { where: { id: ref.id } },
                        })
                      }
                    >
                      <MdDeleteOutline size={20} />
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="pricers-ref__expanded">
                      {renderEntries(ref)}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ReferentialsPage;
